import { ReadonlyVec2, vec2, vec3 } from 'gl-matrix';
import { gl } from '~/engine/Engine';
import { FrameBufferObject } from '~/engine/core/utils/FrameBufferObject';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    RegionOfInterest,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import { RenderStorage } from '~/engine/core/render/RenderStorage';
import { SdfGenerator } from '~/engine/util/algorithms/sdf/SdfGenerator';
import { copyFboToTexture3DSlice } from '~/engine/util/algorithms/sdf/Texture3DUtils';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { ViewManager } from '~/engine/view/ViewManager';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';

/** Polygon data for a single transversal slice. */
export interface DeformSlicePolygons {
    /** Slice position along the image normal direction (mm). */
    normalPosition: number;
    /** Closed polygons in patient space. */
    polygons: vec3[][];
}

/**
 * Maximum Euclidean distance (mm) from the mouse to a
 * polygon vertex for anchor snapping.
 */
const SNAP_RADIUS_MM = 15;

/** Weight threshold below which the gaussian has no effect. */
const WEIGHT_THRESHOLD = 0.01;

/**
 * Axis along which the slice is taken.
 * - 0 = X (sagittal)
 * - 1 = Y (coronal)
 * - 2 = Z (transversal)
 */
export type SliceAxis = 0 | 1 | 2;

/** Compute total arc length of a closed polygon. */
function polygonArcLength(poly: vec3[]): number {
    let total = 0;
    const n = poly.length;
    for (let i = 0; i < n; i++) {
        total += vec3.distance(poly[i], poly[(i + 1) % n]);
    }
    return total;
}

/** Mutable state for an active deform stroke. */
export class LineDeformActionState {
    private readonly structure: RegionOfInterest;
    private readonly view: PlaneView;
    private readonly spreadPercent: number;
    private readonly textureWrite: TextureWrite;

    /** All polygons on the active slice (reference into DeformTool data). */
    private readonly slicePolygons: vec3[][] | undefined;
    /** Undeformed positions of the polygon being dragged. */
    private originalPolygon: vec3[] | undefined;
    /** Index of the polygon being deformed within slicePolygons. */
    private deformedPolygonIndex: number | undefined;
    /** Voxel index of the active slice along {@link sliceAxis}. */
    private readonly sliceIndex: number | undefined;
    /** Axis perpendicular to the slice plane. */
    private readonly sliceAxis: SliceAxis;

    /** Index of the single anchor vertex on the polygon. */
    private anchorIndex: number | undefined;
    /** Pre-computed arc-length distance from anchor per vertex (mm). */
    private arcDist: Float64Array | undefined;
    /** Gaussian sigma derived from spread percentage and polygon arc length. */
    private gamma: number | undefined;
    private strokeStartMouse: vec3;
    private currentMouse: vec3;

    private rafPending = false;
    private _hasModified = false;
    private sliceFBO: FrameBufferObject | undefined;
    private destFBO: FrameBufferObject | undefined;
    /** Last readback from copyFboToTexture3DSlice for coronal/sagittal. */
    private _lastNonZSlicePixels: Uint8Array | undefined;

    public constructor(
        structure: RegionOfInterest,
        image: ImageObject,
        view: PlaneView,
        startCoordinates: ReadonlyVec2,
        spreadPercent: number,
        sliceIndex: number,
        sliceAxis: SliceAxis,
        slicePolygons: vec3[][] | undefined
    ) {
        this.structure = structure;
        this.view = view;
        this.spreadPercent = spreadPercent;
        this.sliceAxis = sliceAxis;

        const patientPos = view.clientToPatientCoordinates(startCoordinates);
        this.strokeStartMouse = vec3.clone(patientPos);
        this.currentMouse = vec3.clone(patientPos);

        this.textureWrite = structure.startModify('deform');

        const texture = structure.texture;
        if (texture && !texture.isEmpty) {
            const sdf = texture.signedDistanceField;
            const axisSize = sdf.size[sliceAxis];
            if (sliceIndex >= 0 && sliceIndex < axisSize) {
                this.sliceIndex = sliceIndex;
                this.initGpuResources(texture);
            }

            if (slicePolygons && slicePolygons.length > 0) {
                this.slicePolygons = slicePolygons;
                this.findAnchor();
            }
        }
    }

    public get targetStructure(): RegionOfInterest {
        return this.structure;
    }

    /** Index of the polygon being deformed, if an anchor has been found. */
    public get activePolygonIndex(): number | undefined {
        return this.deformedPolygonIndex;
    }

    /** Index of the anchor vertex on the polygon. */
    public get activeAnchorIndex(): number | undefined {
        return this.anchorIndex;
    }

    /** Undeformed vertex positions of the polygon being dragged. */
    public get activeOriginalPolygon(): vec3[] | undefined {
        return this.originalPolygon;
    }

    public onMouseMove(coordinates: ReadonlyVec2): EventHandlerResult {
        const patientPos = this.view.clientToPatientCoordinates(coordinates);
        vec3.copy(this.currentMouse, patientPos);
        if (this.anchorIndex === undefined) {
            this.findAnchor();
        }
        if (this.rafPending) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        this.rafPending = true;
        requestAnimationFrame(() => {
            if (!this.rafPending) {
                return;
            }
            this.rafPending = false;
            this.applyDeformation();
            this.view.renderLayers();
            ViewManager.viewUpdateEvent(this.createContourUpdateEvent());
        });
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /** Commit the deformed SDF as the new state. */
    public finish(): void {
        this.flushPendingFrame();
        this.disposeGpuResources();
        if (this._hasModified) {
            const sdf = this.structure.texture?.signedDistanceField;
            if (sdf) {
                if (this.sliceAxis !== 2 && this._lastNonZSlicePixels) {
                    sdf.updateCachedSlice(
                        this.sliceIndex!,
                        this.sliceAxis as 0 | 1,
                        this._lastNonZSlicePixels
                    );
                } else {
                    sdf.syncSliceFromGpu(this.sliceIndex!, this.sliceAxis);
                }
            }
            this.textureWrite.finish();
        } else {
            this.textureWrite.cancel();
        }
    }

    /**
     * Restore the original SDF slice so that cancellation
     * (Escape) leaves the contour unchanged.
     */
    public cancel(): void {
        this.rafPending = false;
        // Restore the original vertex positions.
        if (
            this.slicePolygons &&
            this.originalPolygon &&
            this.deformedPolygonIndex !== undefined
        ) {
            const target = this.slicePolygons[this.deformedPolygonIndex];
            for (let i = 0; i < target.length; i++) {
                vec3.copy(target[i], this.originalPolygon[i]);
            }
            const texture = this.structure.texture;
            if (texture && !texture.isEmpty && this.sliceIndex !== undefined) {
                this.renderPolygonsToSdf(texture, this.slicePolygons);
            }
        }
        this.disposeGpuResources();
        this.textureWrite.cancel();
    }

    /**
     * Finds the single closest vertex to the current
     * mouse within the snap radius. Sets the anchor
     * index, selects that polygon for deformation, and
     * pre-computes arc-length distances. Called lazily
     * until an anchor is found.
     */
    private findAnchor(): void {
        if (this.anchorIndex !== undefined || !this.slicePolygons) {
            return;
        }

        const snapRadiusSq = SNAP_RADIUS_MM * SNAP_RADIUS_MM;

        let bestDistSq = Infinity;
        let bestPi = -1;
        let bestVi = -1;

        for (let pi = 0; pi < this.slicePolygons.length; pi++) {
            const polygon = this.slicePolygons[pi];
            for (let vi = 0; vi < polygon.length; vi++) {
                const distSq = vec3.squaredDistance(
                    polygon[vi],
                    this.currentMouse
                );
                if (distSq <= snapRadiusSq && distSq < bestDistSq) {
                    bestDistSq = distSq;
                    bestPi = pi;
                    bestVi = vi;
                }
            }
        }

        if (bestPi < 0) {
            return;
        }

        this.deformedPolygonIndex = bestPi;
        const poly = this.slicePolygons[bestPi];
        this.originalPolygon = poly.map((v) => vec3.clone(v));
        this.anchorIndex = bestVi;
        vec3.copy(this.strokeStartMouse, this.currentMouse);

        // Compute gamma from spread percentage and polygon arc length.
        // halfArc is the arc distance from anchor where the
        // effect should reach the weight threshold.
        const totalArc = polygonArcLength(poly);
        const halfArc = (totalArc * this.spreadPercent) / 200;
        // gamma such that exp(-halfArc²/(2*gamma²)) = WEIGHT_THRESHOLD
        // => gamma = halfArc / sqrt(-2 * ln(WEIGHT_THRESHOLD))
        this.gamma = Math.max(
            1e-6,
            halfArc / Math.sqrt(-2 * Math.log(WEIGHT_THRESHOLD))
        );

        // Pre-compute per-vertex arc-length distance
        // from the anchor (shortest of CW / CCW path).
        const n = poly.length;
        const halfN = Math.floor(n / 2);
        const dist = new Float64Array(n);
        dist.fill(Infinity);
        dist[bestVi] = 0;

        // Walk clockwise from anchor.
        let arcCw = 0;
        for (let d = 1; d <= halfN; d++) {
            const prevIdx = (bestVi + d - 1) % n;
            const curIdx = (bestVi + d) % n;
            arcCw += vec3.distance(poly[prevIdx], poly[curIdx]);
            dist[curIdx] = arcCw;
        }

        // Walk counter-clockwise; keep the shorter path.
        let arcCcw = 0;
        for (let d = 1; d <= halfN; d++) {
            const prevIdx = (bestVi - d + 1 + n) % n;
            const curIdx = (bestVi - d + n) % n;
            arcCcw += vec3.distance(poly[prevIdx], poly[curIdx]);
            if (arcCcw < dist[curIdx]) {
                dist[curIdx] = arcCcw;
            }
        }

        this.arcDist = dist;
    }

    private applyDeformation(): void {
        if (
            !this.originalPolygon ||
            !this.slicePolygons ||
            !this.arcDist ||
            this.deformedPolygonIndex === undefined ||
            this.gamma === undefined
        ) {
            return;
        }

        const texture = this.structure.texture;
        if (!texture || texture.isEmpty || this.sliceIndex === undefined) {
            return;
        }

        const displacement = vec3.sub(
            vec3.create(),
            this.currentMouse,
            this.strokeStartMouse
        );
        if (vec3.squaredLength(displacement) < 1e-12) {
            return;
        }

        this._hasModified = true;
        const target = this.slicePolygons[this.deformedPolygonIndex];
        const gammaSq = this.gamma * this.gamma;
        const n = this.originalPolygon.length;

        for (let vi = 0; vi < n; vi++) {
            const d = this.arcDist[vi];
            const weight = Math.exp(-(d * d) / (2 * gammaSq));

            vec3.scaleAndAdd(
                target[vi],
                this.originalPolygon[vi],
                displacement,
                weight
            );
        }

        this.renderPolygonsToSdf(texture, this.slicePolygons);
    }

    /** Planar size (pixels) of the 2D slice for the current axis. */
    private slicePlanarSize(grid: {
        sizeX: number;
        sizeY: number;
        sizeZ: number;
    }): vec2 {
        switch (this.sliceAxis) {
            case 0:
                return vec2.fromValues(grid.sizeY, grid.sizeZ);
            case 1:
                return vec2.fromValues(grid.sizeX, grid.sizeZ);
            case 2:
                return vec2.fromValues(grid.sizeX, grid.sizeY);
        }
    }

    private initGpuResources(texture: ContourTexture): void {
        const grid = texture.textureGrid;
        const size = this.slicePlanarSize(grid);

        this.sliceFBO = new FrameBufferObject(size);
        this.sliceFBO.createTextureAttachment(
            gl.COLOR_ATTACHMENT0,
            gl.R8,
            gl.RED,
            gl.UNSIGNED_BYTE
        );
        this.sliceFBO.createBufferAttachment(
            gl.DEPTH_STENCIL_ATTACHMENT,
            gl.DEPTH24_STENCIL8
        );
        this.sliceFBO.checkStatus();

        this.destFBO = new FrameBufferObject(size);
        this.destFBO.unbind();
    }

    /**
     * Pick the two clip-space components that correspond to
     * the 2D plane of the current slice axis.
     */
    private clipToNdc(clip: vec3): vec2 {
        const clamp = (v: number): number => Math.max(-1, Math.min(1, v));
        switch (this.sliceAxis) {
            case 0:
                return vec2.fromValues(clamp(clip[1]), clamp(clip[2]));
            case 1:
                return vec2.fromValues(clamp(clip[0]), clamp(clip[2]));
            case 2:
                return vec2.fromValues(clamp(clip[0]), clamp(clip[1]));
        }
    }

    /** Half physical size (mm) of the slice plane. */
    private sliceHalfSizeMm(grid: {
        sizeXMM: number;
        sizeYMM: number;
        sizeZMM: number;
    }): vec2 {
        switch (this.sliceAxis) {
            case 0:
                return vec2.fromValues(grid.sizeYMM / 2, grid.sizeZMM / 2);
            case 1:
                return vec2.fromValues(grid.sizeXMM / 2, grid.sizeZMM / 2);
            case 2:
                return vec2.fromValues(grid.sizeXMM / 2, grid.sizeYMM / 2);
        }
    }

    /**
     * Render polygons into the SDF texture at the current
     * slice using the pre-created FBOs.
     */
    private renderPolygonsToSdf(
        texture: ContourTexture,
        polygons: vec3[][]
    ): void {
        if (!this.sliceFBO || !this.destFBO || this.sliceIndex === undefined) {
            return;
        }

        const grid = texture.textureGrid;
        const ndcPolygons: vec2[][] = [];
        for (const polygon of polygons) {
            const ndc: vec2[] = [];
            for (const vertex of polygon) {
                const clip = grid.worldToClipSpacePosition(vertex);
                ndc.push(this.clipToNdc(clip));
            }
            ndcPolygons.push(ndc);
        }

        const halfSizeMm = this.sliceHalfSizeMm(grid);
        const sdfGen = RenderStorage.getRenderer(SdfGenerator);
        const innerDist = sdfGen.computeNormalizedDistances(
            ContourTexture.DEFAULT_MARGIN,
            halfSizeMm
        );
        const outerDist = sdfGen.computeNormalizedDistances(
            ContourTexture.DEFAULT_MARGIN,
            halfSizeMm
        );
        const planarSize = this.slicePlanarSize(grid);

        const vaos = sdfGen.generateVAOsFromPolygons(
            ndcPolygons,
            innerDist,
            outerDist
        );

        if (this.sliceAxis === 2) {
            // Transversal: write directly to the Z-layer.
            sdfGen.renderSlicePipeline(
                vaos,
                innerDist,
                outerDist,
                this.sliceFBO,
                this.destFBO,
                texture.signedDistanceField.texture,
                this.sliceIndex,
                planarSize
            );
        } else {
            // Coronal / sagittal: render to FBO, read back,
            // then scatter into the 3D texture with texSubImage3D.
            sdfGen.renderSliceToFbo(
                vaos,
                innerDist,
                outerDist,
                this.sliceFBO,
                planarSize
            );

            this._lastNonZSlicePixels = copyFboToTexture3DSlice(
                this.sliceFBO,
                texture.signedDistanceField.texture,
                this.sliceAxis as 0 | 1,
                this.sliceIndex,
                planarSize[0],
                planarSize[1]
            );
        }
    }

    /**
     * If a rAF callback is pending, run the deformation
     * synchronously so the final state is committed before
     * the stroke ends.
     */
    private flushPendingFrame(): void {
        if (!this.rafPending) {
            return;
        }
        this.rafPending = false;
        this.applyDeformation();
        this.view.renderLayers();
        ViewManager.viewUpdateEvent(this.createContourUpdateEvent());
    }

    private disposeGpuResources(): void {
        this.sliceFBO?.delete();
        this.destFBO?.delete();
    }

    private createContourUpdateEvent(): VolumeViewUpdateEvent {
        return new VolumeViewUpdateEvent(
            this.structure.boundingBox,
            LayerBit.CONTOUR_LAYER_BIT,
            true
        );
    }
}

/**
 * {@link LineDeformAction} manages the lifecycle of a deform stroke.
 * It creates a {@link LineDeformActionState} on stroke start and
 * finishes/clears it on stroke end.
 */
export class LineDeformAction {
    private readonly structure: RegionOfInterest;
    private readonly image: ImageObject;
    private readonly spreadPercent: number;
    private readonly sliceIndex: number;
    private readonly sliceAxis: SliceAxis;
    private readonly slicePolygons: vec3[][] | undefined;

    public constructor(
        structure: RegionOfInterest,
        image: ImageObject,
        spreadPercent: number,
        sliceIndex: number,
        sliceAxis: SliceAxis,
        slicePolygons: vec3[][] | undefined
    ) {
        this.structure = structure;
        this.image = image;
        this.spreadPercent = spreadPercent;
        this.sliceIndex = sliceIndex;
        this.sliceAxis = sliceAxis;
        this.slicePolygons = slicePolygons;
    }

    public startAction(
        view: PlaneView,
        coordinates: ReadonlyVec2
    ): LineDeformActionState {
        return new LineDeformActionState(
            this.structure,
            this.image,
            view,
            coordinates,
            this.spreadPercent,
            this.sliceIndex,
            this.sliceAxis,
            this.slicePolygons
        );
    }

    public stopAction(state: LineDeformActionState): EventHandlerResult {
        state.finish();
        const bb = state.targetStructure.boundingBox;
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true,
            new VolumeViewUpdateEvent(bb, LayerBit.CONTOUR_LAYER_BIT, true)
        );
    }
}
